import React, { useState, useRef, useEffect } from 'react';
import { SolutionValidatorService } from '../../../services/solution-validator/solution-validator.service';
import { Message } from '../useExercisePractice';
import { isMobileDevice, compressImage } from '../../../utils/utils';
import { validateIcon } from '../../../assets/images';
import { CaptureService } from '../../../services/capture-exercise/capture-exercise.service';
import { getApiUrl } from '../../../configs/config';
import { gamificationStore } from '../../../utils/gamificationStore';
import { useQuestionsStore } from '../../../utils/questionStore';
import { useStepsStore } from '../../../utils/stepStore';
import { ExerciseReviewData } from '../../../components/exercise-review-content/types';

export interface CapturedPage {
  blob: Blob;
  url: string;
}

export interface UseSolutionCaptureProps {
  exerciseId: number | null;
  selectedQuestionId: number | null;
  selectedQuestionLetter: string | null;
  activeQuestionSteps: any[];
  activeStepIndex: number;
  addBotMessage: (msg: Partial<Message>, delay?: number) => Promise<string>;
  setMessages: React.Dispatch<React.SetStateAction<Message[]>>;
  t: (key: string, options?: any) => string;
  user_attributes: any;
  handleShowStepSolution: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  handleStepSolved: (index: number, stepsList: any[], qId: number, qLetter: string) => void;
  studentName: string;
  setExerciseQuestions?: React.Dispatch<React.SetStateAction<any[]>>;
  exerciseTitle?: string;
  topicName?: string;
  difficultyName?: string;
  levelUnits?: number | null;
  questionText?: string;
}

export const useSolutionCapture = ({
  exerciseId,
  selectedQuestionId,
  selectedQuestionLetter,
  activeQuestionSteps,
  activeStepIndex,
  addBotMessage,
  setMessages,
  t,
  user_attributes,
  handleShowStepSolution,
  handleStepSolved,
  studentName,
  setExerciseQuestions,
  exerciseTitle,
  topicName,
  difficultyName,
  levelUnits,
  questionText
}: UseSolutionCaptureProps) => {
  const [isUploading, setIsUploading] = useState(false);
  const [capturedPages, setCapturedPages] = useState<CapturedPage[]>([]);
  const [solutionAnalysisData, setSolutionAnalysisData] = useState<ExerciseReviewData | null>(null);
  const [isAnalysisModalOpen, setIsAnalysisModalOpen] = useState(false);
  const captureAttempt = useRef<number>(0);
  const pagesRef = useRef<CapturedPage[]>([]);
  const downloadedUrlsRef = useRef<string[]>([]);
  const isResettingRef = useRef<boolean>(false);
  
  const [qrSessionId, setQrSessionId] = useState<string | null>(null);
  const [isMobileActive, setIsMobileActive] = useState(false);
  const [isPolling, setIsPolling] = useState(false);
  const [fileToCrop, setFileToCrop] = useState<File | null>(null);

  useEffect(() => {
    pagesRef.current = capturedPages;
  }, [capturedPages]);

  useEffect(() => {
    setIsPolling(false);
    setIsMobileActive(false);
    setQrSessionId(null);
    setCapturedPages([]);
    pagesRef.current = [];
    downloadedUrlsRef.current = [];
    setSolutionAnalysisData(null);
    setIsAnalysisModalOpen(false);
  }, [selectedQuestionId, exerciseId]);

  const cleanupObjectURLs = (pages: CapturedPage[]) => {
    pages.forEach(page => {
      if (page.url) {
        URL.revokeObjectURL(page.url);
      }
    });
  };

  useEffect(() => {
    return () => {
      cleanupObjectURLs(pagesRef.current);
    };
  }, []);

  useEffect(() => {
    captureAttempt.current = 0;
    resetCapture();
  }, [selectedQuestionId]);

  const startManualCapture = () => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = 'image/*';
    if (isMobileDevice()) {
      input.setAttribute('capture', 'environment');
    }
    input.onchange = (event: any) => {
      const files = Array.from(event.target.files || []) as File[];
      if (files.length > 0) {
        setFileToCrop(files[0]);
      }
    };
    input.click();
  };

  const handleCroppedFile = async (croppedBlob: Blob) => {
    try {
      const compressed = await compressImage(croppedBlob);
      const url = URL.createObjectURL(compressed);
      const newPage: CapturedPage = { blob: compressed, url };
      
      const updatedPages = [...pagesRef.current, newPage];
      pagesRef.current = updatedPages;
      setCapturedPages(updatedPages);

      // Add/update captured solution images message
      const imgMessageId = 'captured-solution-images';
      setMessages(prev => {
        const nonImageMsgs = prev.filter(m => m.id !== imgMessageId);
        return [
          ...nonImageMsgs,
          {
            id: imgMessageId,
            sender: 'user',
            text: '',
            type: 'captured_image',
            imageUrls: updatedPages.map(p => p.url)
          }
        ];
      });

      showConfirmationMessage(updatedPages);
    } catch (err) {
      console.error('Failed to process cropped file:', err);
    } finally {
      setFileToCrop(null);
    }
  };

  const startCapture = async (isAdding = false) => {
    if (isMobileDevice()) {
      startManualCapture();
      return;
    }

    // Instantly reset old state to prevent leakage during token generation
    setIsPolling(false);
    
    if (!isAdding) {
      setIsMobileActive(false);
      cleanupObjectURLs(pagesRef.current);
      pagesRef.current = [];
      setCapturedPages([]);
      downloadedUrlsRef.current = [];
    }

    const sessionId = isAdding && qrSessionId 
      ? qrSessionId 
      : `${Date.now()}-${Math.random().toString(36).substring(2, 9)}`;

    // 🚀 Instantly insert QR card container at 0ms with loading state
    setMessages(prev => [
      ...prev.filter(m => m.id !== 'captured-solution-qr'),
      {
        id: 'captured-solution-qr',
        sender: 'bot',
        text: '',
        type: 'qr_code',
        qrSessionId: sessionId,
        isGenerating: true
      }
    ]);

    try {
      setIsUploading(true);
      
      const captureService = new CaptureService();
      const tokenRes = await captureService.GENERATETEMPTOKEN(sessionId);
      const token = tokenRes.token;
      
      const qrUrl = `${window.location.origin}/capture-exercise?type=solution&sessionId=${sessionId}&token=${token}`;
      
      setQrSessionId(sessionId);
      setIsPolling(true);
      
      // Update QR card with final URL
      setMessages(prev => prev.map(m => {
        if (m.id === 'captured-solution-qr') {
          return {
            ...m,
            text: qrUrl,
            isGenerating: false
          };
        }
        return m;
      }));
    } catch (err) {
      console.error("Failed to generate QR session:", err);
      setMessages(prev => prev.filter(m => m.id !== 'captured-solution-qr'));
      await addBotMessage({
        id: `qr-generation-error-${Date.now()}`,
        text: t('qrCodeGenerationFailed')
      });
    } finally {
      setIsUploading(false);
    }
  };

  const resetCapture = async () => {
    setIsPolling(false);
    isResettingRef.current = true;

    cleanupObjectURLs(pagesRef.current);
    pagesRef.current = [];
    setCapturedPages([]);
    downloadedUrlsRef.current = [];
    setIsMobileActive(false);
    
    setMessages(prev => prev.filter(m => 
      m.id !== 'captured-solution-images' && 
      m.id !== 'captured-solution-confirm-options' &&
      m.id !== 'captured-solution-qr'
    ));

    if (qrSessionId) {
      const captureService = new CaptureService();
      try {
        await captureService.resetUserImageSolution(qrSessionId);
      } catch (err) {
        console.error("Failed to reset session images on server:", err);
      }
      isResettingRef.current = false;
      setIsPolling(true);
    } else {
      isResettingRef.current = false;
    }
  };

  const removePage = (index: number) => {
    const pageToRemove = pagesRef.current[index];
    if (pageToRemove) {
      URL.revokeObjectURL(pageToRemove.url);
    }
    const updatedPages = pagesRef.current.filter((_, i) => i !== index);
    pagesRef.current = updatedPages;
    setCapturedPages(updatedPages);

    if (updatedPages.length === 0) {
      setMessages(prev => prev.filter(m => m.id !== 'captured-solution-images' && m.id !== 'captured-solution-confirm-options'));
    } else {
      setMessages(prev =>
        prev.map(m => {
          if (m.id === 'captured-solution-images') {
            return {
              ...m,
              imageUrls: updatedPages.map(p => p.url)
            };
          }
          return m;
        })
      );
      showConfirmationMessage(updatedPages);
    }
  };

  const handleFilesSelected = async (files: File[]) => {
    if (pagesRef.current.length + files.length > 5) {
      alert(t('solutionCaptureLimitReached'));
      return;
    }

    const newPages: CapturedPage[] = [];
    for (const file of files) {
      try {
        const compressed = await compressImage(file);
        const url = URL.createObjectURL(compressed);
        newPages.push({ blob: compressed, url });
      } catch (err) {
        console.error('Failed to compress image:', err);
        const url = URL.createObjectURL(file);
        newPages.push({ blob: file, url });
      }
    }

    const updatedPages = [...pagesRef.current, ...newPages];
    pagesRef.current = updatedPages;
    setCapturedPages(updatedPages);
    
    // Add captured image message to chat (or update existing)
    const imgMessageId = 'captured-solution-images';
    setMessages(prev => {
      const nonImageMsgs = prev.filter(m => m.id !== imgMessageId);
      return [
        ...nonImageMsgs,
        {
          id: imgMessageId,
          sender: 'user',
          text: '',
          type: 'captured_image',
          imageUrls: updatedPages.map(p => p.url)
        }
      ];
    });

    showConfirmationMessage(updatedPages);
  };

  const showConfirmationMessage = (pages: CapturedPage[]) => {
    const genderNum = user_attributes?.gender;
    const genderStr = genderNum === 2 ? 'female' : 'male';
    const confirmText = t(`solutionCaptureConfirmQuality_${genderStr}`, { studentName, count: pages.length });
    const confirmMsgId = 'captured-solution-confirm-options';

    setMessages(prev => prev.filter(m => m.id !== confirmMsgId));

    addBotMessage({
      id: confirmMsgId,
      text: confirmText,
      type: 'options',
      optionsClassName: 'solution-capture-options-layout',
      options: [
        {
          label: t('solutionCaptureAddPage'),
          value: 'add_page',
          action: () => startCapture(true),
          disabled: pages.length >= 5,
          color: 'dark' as const,
          variant: 'outlined' as const
        },
        {
          label: t('solutionCaptureReset'),
          value: 'reset_capture',
          action: () => resetCapture(),
          color: 'dark' as const,
          variant: 'outlined' as const
        },
        {
          label: t('solutionCaptureApprove'),
          value: 'approve_quality',
          action: () => handleConfirmCapture(pagesRef.current),
          color: 'black' as const,
          variant: 'contained' as const,
          icon: validateIcon
        }
      ]
    });
  };

  const handleLowOcrConfidence = async (message?: string, retryInstruction?: string) => {
    captureAttempt.current += 1;
    const genderNum = user_attributes?.gender;
    const genderStr = genderNum === 2 ? 'female' : 'male';

    if (captureAttempt.current < 2) {
      let failMsg = '';
      if (message) {
        failMsg = message;
        if (retryInstruction) {
          failMsg = `${failMsg.trim()} ${retryInstruction.trim()}`;
        }
      } else {
        failMsg = t(`solutionCaptureOcrFailFirst_${genderStr}`, { studentName });
      }

      await addBotMessage({
        id: `ocr-fail-retry-msg-${Date.now()}`,
        text: failMsg,
        type: 'options',
        options: [
          {
            label: t('solutionCaptureRetake'),
            value: 'retake_quality',
            action: () => {
              resetCapture();
              startCapture(false);
            }
          }
        ]
      });
    } else {
      captureAttempt.current = 0;
      
      let failMsg = '';
      if (message) {
        failMsg = message;
        const finalInstructions = t(`solutionCaptureOcrFailFinalInstructions_${genderStr}`);
        failMsg = `${failMsg.trim()}<br/>${finalInstructions}`;
      } else {
        failMsg = t(`solutionCaptureOcrFailFinal_${genderStr}`, { studentName });
      }

      await addBotMessage({
        id: `ocr-fail-final-msg-${Date.now()}`,
        text: failMsg,
        type: 'options',
        options: [
          {
            label: t('exercisePracticeSolutionBtn'),
            value: 'solution',
            action: () => {
              cleanupObjectURLs(pagesRef.current);
              pagesRef.current = [];
              setCapturedPages([]);
              handleShowStepSolution(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'black' as const,
            variant: 'contained' as const
          },
          {
            label: t('understoodNextStep'),
            value: 'next_step',
            action: () => {
              cleanupObjectURLs(pagesRef.current);
              pagesRef.current = [];
              setCapturedPages([]);
              handleStepSolved(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'dark' as const,
            variant: 'outlined' as const
          }
        ]
      });
    }
  };

  const handleConfirmCapture = async (pagesToSubmit?: CapturedPage[]) => {
    const activePages = pagesToSubmit || pagesRef.current;
    if (activePages.length === 0 || !exerciseId || isUploading) return;

    setIsPolling(false);
    setIsUploading(true);
    const loaderMessageId = `solution-upload-loader-${Date.now()}`;
    const genderNum = user_attributes?.gender;
    const genderStr = genderNum === 2 ? 'female' : 'male';
    const loadingText = t(`solutionCaptureProcessing_${genderStr}`, { studentName });

    setMessages(prev => [
      ...prev,
      {
        id: loaderMessageId,
        sender: 'bot',
        text: loadingText,
        type: 'loader_bubble',
        loadingText: loadingText
      }
    ]);

    const service = new SolutionValidatorService();
    try {
      const blobs = activePages.map(p => p.blob);
      const response = await service.uploadSolution(blobs, exerciseId, selectedQuestionId || undefined);
      
      setMessages(prev => prev.filter(m => m.id !== loaderMessageId));

      if (response.status === 'success' || response.status === 'incorrect') {
        if (response.detailed_feedback) {
          const primaryQ = response.detailed_feedback.questions?.[0];
          const normalizedData: ExerciseReviewData = {
            exerciseId: exerciseId || undefined,
            exerciseTitle: exerciseTitle || "",
            topicName: topicName || "",
            difficultyName: difficultyName || "",
            levelUnits: levelUnits,
            questionText: questionText || primaryQ?.question_text || "",
            questionLetter: selectedQuestionLetter || "",
            status: primaryQ?.status || (response.status === 'success' ? 'correct' : 'incorrect'),
            overallScore: response.overall_score,
            analysisSummary: response.detailed_feedback.general_notes || response.summary_text || "",
            strengths: (primaryQ as any)?.strengths?.length ? (primaryQ as any).strengths : (response.detailed_feedback.strengths || []),
            weaknesses: (primaryQ as any)?.weaknesses?.length ? (primaryQ as any).weaknesses : (response.detailed_feedback.weaknesses || []),
            issues: (primaryQ as any)?.issues?.length ? (primaryQ as any).issues : (response.detailed_feedback.issues || response.detailed_feedback.recommended_topics || []),
            solvedDate: new Date().toISOString()
          };
          setSolutionAnalysisData(normalizedData);
        }

        if (exerciseId && selectedQuestionId) {
          useQuestionsStore.getState().addCompletedQuestion(exerciseId, selectedQuestionId);
          const resolvedStepId = activeQuestionSteps[activeStepIndex]?.id;
          if (resolvedStepId) {
            useStepsStore.getState().addCompletedStep(selectedQuestionId, resolvedStepId);
          }
          if (setExerciseQuestions) {
            setExerciseQuestions(prev => prev.map(q => q.id === selectedQuestionId ? { ...q, isCompleted: true } : q));
          }
        }
      }

      if (response.status === 'success') {
        captureAttempt.current = 0;

        if (response.xp_awarded && response.xp_awarded > 0) {
          const currentTotal = gamificationStore.getState().totalPoints || 0;
          gamificationStore.getState().setGamificationState({
            totalPoints: currentTotal + response.xp_awarded,
            lastUpdatedAt: 0
          });
          gamificationStore.getState().initState();
        }
        
        const mainSuccessSentence = t(`solutionCaptureValidationSuccess_${genderStr}`);
        const successActionText = t('solutionCaptureValidationSuccessAction');

        const successBannerContent = React.createElement(
          'span',
          { className: 'v2-success-banner__success-content' },
          React.createElement('span', { className: 'v2-success-banner__main-sentence' }, mainSuccessSentence),
          response.detailed_feedback
            ? React.createElement(
                'span',
                {
                  className: 'v2-success-banner__action-link v2-success-banner__action-link--small',
                  onClick: () => setIsAnalysisModalOpen(true),
                  role: 'button',
                  tabIndex: 0
                },
                successActionText
              )
            : null
        );

        const bannerMsgId = `success-banner-${Date.now()}`;
        setMessages(prev => [
          ...prev,
          {
            id: bannerMsgId,
            sender: 'bot',
            text: '',
            type: 'success_banner',
            points: response.xp_awarded,
            bannerMessage: successBannerContent
          }
        ]);

        const successOptions = [
          {
            label: t('solutionByShunit'),
            value: 'solution',
            action: () => {
              cleanupObjectURLs(activePages);
              pagesRef.current = [];
              setCapturedPages([]);
              handleShowStepSolution(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'black' as const,
            variant: 'contained' as const
          },
          {
            label: t('solutionCaptureUnderstoodNextStep'),
            value: 'next_step',
            action: () => {
              cleanupObjectURLs(activePages);
              pagesRef.current = [];
              setCapturedPages([]);
              handleStepSolved(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'dark' as const,
            variant: 'outlined' as const
          }
        ];

        await addBotMessage({
          id: `validation-success-msg-${Date.now()}`,
          type: 'options',
          optionsClassName: 'solution-actions-row-layout',
          options: successOptions
        });
      } else if (response.status === 'low_ocr_confidence') {
        await handleLowOcrConfidence(response.summary_text, (response as any).retry_instruction);
      } else if (response.status === 'incorrect') {
        captureAttempt.current = 0;
        
        const greetingText = t(`solutionCaptureValidationFailGreeting_${genderStr}`, { 
          studentName: studentName || (genderStr === 'female' ? t('studentFemale') : t('studentMale')) 
        });
        const failBodyText = t('solutionCaptureValidationFailBody');
        const failActionText = t(`solutionCaptureValidationFailAction_${genderStr}`);

        const bannerContent = React.createElement(
          'span',
          { className: 'v2-success-banner__fail-content' },
          React.createElement('span', { className: 'banner-greeting' }, greetingText),
          React.createElement('br', null),
          React.createElement('span', { className: 'banner-body' }, failBodyText),
          React.createElement(
            'span',
            {
              className: 'v2-success-banner__action-link',
              onClick: () => setIsAnalysisModalOpen(true),
              role: 'button',
              tabIndex: 0
            },
            failActionText
          )
        );

        const bannerMsgId = `incorrect-banner-${Date.now()}`;
        setMessages(prev => [
          ...prev,
          {
            id: bannerMsgId,
            sender: 'bot',
            text: '',
            type: 'success_banner',
            variant: 'error',
            points: 0,
            bannerMessage: bannerContent
          }
        ]);

        const incorrectOptions = [
          {
            label: t('solutionByShunit'),
            value: 'solution',
            action: () => {
              cleanupObjectURLs(activePages);
              pagesRef.current = [];
              setCapturedPages([]);
              handleShowStepSolution(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'black' as const,
            variant: 'contained' as const
          },
          {
            label: t('understoodNextStep'),
            value: 'next_step',
            action: () => {
              cleanupObjectURLs(activePages);
              pagesRef.current = [];
              setCapturedPages([]);
              handleStepSolved(activeStepIndex, activeQuestionSteps, selectedQuestionId!, selectedQuestionLetter || '');
            },
            color: 'dark' as const,
            variant: 'outlined' as const
          }
        ];

        await addBotMessage({
          id: `validation-incorrect-msg-${Date.now()}`,
          type: 'options',
          optionsClassName: 'solution-actions-row-layout',
          options: incorrectOptions
        });
      } else {
        captureAttempt.current = 0;
        const serverMsg = (response as any)?.message || (response as any)?.detail || t('generalError');
        await addBotMessage({
          id: `validation-error-msg-${Date.now()}`,
          text: serverMsg
        });
      }
    } catch (error: any) {
      console.error("Upload solution error:", error);
      setMessages(prev => prev.filter(m => m.id !== loaderMessageId));
      
      const errorDetail = error.response?.data?.detail;
      if (errorDetail && errorDetail.error_code === 'low_ocr_confidence') {
        await handleLowOcrConfidence(errorDetail.message, errorDetail.retry_instruction);
      } else {
        const errorMsg = typeof errorDetail === 'string'
          ? errorDetail
          : (errorDetail?.message || error.response?.data?.message || t('generalError'));
        await addBotMessage({
          id: `validation-error-msg-catch-${Date.now()}`,
          text: errorMsg
        });
      }
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    if (!isPolling || !qrSessionId) return;

    const controller = new AbortController();
    let timeoutId: NodeJS.Timeout | null = null;
    let intervalId: NodeJS.Timeout | null = null;

    const stopPollingAndCleanup = () => {
      setIsPolling(false);
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const genderNum = user_attributes?.gender;
    const genderSuffix = genderNum === 2 ? '_female' : '_male';

    // Hard timeout of 5 minutes
    timeoutId = setTimeout(() => {
      stopPollingAndCleanup();
      // Remove QR code message
      setMessages(prev => prev.filter(m => m.id !== 'captured-solution-qr'));
      // Add error bubble
      addBotMessage({
        id: `qr-timeout-error-${Date.now()}`,
        text: t('solutionCaptureTimeout' + genderSuffix),
        type: 'error_bubble',
        errorContext: 'qr_timeout',
      });
    }, 5 * 60 * 1000);

    const captureService = new CaptureService();

    const checkSolution = async () => {
      if (isResettingRef.current) return;
      try {
        const response = await captureService.getUserImageSolution(qrSessionId);
        if (isResettingRef.current) return;
        if (response) {
          if (response.is_active) {
            setIsMobileActive(true);
          }
          if (response.images && response.images.length > 0) {
            const currentPaths = response.images.map((url: string) => url.split('?')[0]);
            const alreadyDownloadedPaths = downloadedUrlsRef.current.map((url: string) => url.split('?')[0]);
            const isSame = currentPaths.length === alreadyDownloadedPaths.length &&
              currentPaths.every((path, i) => path === alreadyDownloadedPaths[i]);
            if (isSame) return;

            downloadedUrlsRef.current = response.images;
            
            // Remove QR code message
            setMessages(prev => prev.filter(m => m.id !== 'captured-solution-qr'));

            // Show loader bubble on desktop
            const loaderMessageId = `solution-download-loader-${Date.now()}`;
            const loadingText = t("downloadingFromMobile");

            setMessages(prev => [
              ...prev.filter(m => m.id !== loaderMessageId),
              {
                id: loaderMessageId,
                sender: 'bot',
                text: loadingText,
                type: 'loader_bubble',
                loadingText: loadingText
              }
            ]);

            // Download images via SAS URLs and convert to Blobs
            try {
              const pages: CapturedPage[] = await Promise.all(
                response.images.map(async (url: string, index: number) => {
                  const proxyUrl = `${getApiUrl()}/capture/proxy-image?url=${encodeURIComponent(url)}`;
                  const res = await fetch(proxyUrl, { credentials: 'include' });
                  const blob = await res.blob();
                  const localUrl = URL.createObjectURL(blob);
                  return { blob, url: localUrl };
                })
              );

              cleanupObjectURLs(pagesRef.current);
              pagesRef.current = pages;
              setMessages(prev => prev.filter(m => m.id !== loaderMessageId));
              setCapturedPages(pages);

              // Add captured image message to chat
              const imgMessageId = 'captured-solution-images';
              setMessages(prev => {
                const nonImageMsgs = prev.filter(m => m.id !== imgMessageId);
                return [
                  ...nonImageMsgs,
                  {
                    id: imgMessageId,
                    sender: 'user',
                    text: '',
                    type: 'captured_image',
                    imageUrls: pages.map(p => p.url)
                  }
                ];
              });

              showConfirmationMessage(pages);
            } catch (err) {
              console.error("Error downloading/converting solution images:", err);
              setMessages(prev => prev.filter(m => m.id !== loaderMessageId));
              addBotMessage({
                id: `qr-download-error-${Date.now()}`,
                text: t('generalError')
              });
            }
          }
        }
      } catch (err: any) {
        console.error("Error polling user image solution:", err);
      }
    };

    // Poll every 3 seconds
    intervalId = setInterval(checkSolution, 3000);

    return () => {
      if (intervalId) clearInterval(intervalId);
      if (timeoutId) clearTimeout(timeoutId);
      controller.abort();
    };
  }, [isPolling, qrSessionId, t, studentName, user_attributes]);

  const stopCapture = () => {
    setIsPolling(false);
    setIsMobileActive(false);
    setQrSessionId(null);
    setCapturedPages([]);
    pagesRef.current = [];
    downloadedUrlsRef.current = [];
    setMessages(prev => prev.filter(m => 
      m.id !== 'captured-solution-images' && 
      m.id !== 'captured-solution-confirm-options' &&
      m.id !== 'captured-solution-qr'
    ));
  };

  return {
    startCapture,
    stopCapture,
    isUploading,
    captureAttempt,
    capturedPages,
    removePage,
    handleConfirmCapture,
    isMobileActive,
    qrSessionId,
    startManualCapture,
    isPolling,
    fileToCrop,
    setFileToCrop,
    handleCroppedFile,
    solutionAnalysisData,
    setSolutionAnalysisData,
    isAnalysisModalOpen,
    setIsAnalysisModalOpen
  };
};
